/**
 * clientErrorReporter — single ingress for every kind of client-side
 * failure (axios rejection, render crash, unhandled JS error,
 * unhandled promise rejection, stale active-operation resume).
 *
 * Operator brief (Feb 19, 2026):
 *   "No white-screen death states. Errors must be traceable. Users
 *    should always get a calm recovery path."
 *
 * Behaviour:
 *   • Generates a short, readable ref ID locally (ERR-XXXXXX) so the
 *     UI can render it immediately even if the report POST fails.
 *   • Classifies the error into one of the ten taxonomy categories.
 *   • Fires-and-forgets a POST to /api/debug/client-errors. Never
 *     throws. Never blocks the caller.
 *   • Tags `error.refId` + `error.category` on axios error objects
 *     so pages can surface them without re-classifying.
 *
 * Stack traces:
 *   • Always sent to the backend (server stores them; admin GET
 *     endpoint exposes them).
 *   • Visible in the UI ONLY when isPreviewEnv() is true.
 */
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const APP_VERSION =
  process.env.REACT_APP_VERSION
  || process.env.REACT_APP_COMMIT_SHA
  || "dev";

const REF_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";


// ── Ref ID generation ──────────────────────────────────────────────
export function generateRefId() {
  let out = "ERR-";
  for (let i = 0; i < 6; i += 1) {
    out += REF_ALPHABET.charAt(
      Math.floor(Math.random() * REF_ALPHABET.length),
    );
  }
  return out;
}


// ── Environment helpers ────────────────────────────────────────────
export function currentEnvironment() {
  const host = (typeof window !== "undefined" && window.location?.hostname) || "";
  if (host.includes("preview.")) return "preview";
  if (process.env.NODE_ENV === "production") return "production";
  if (process.env.NODE_ENV === "development") return "development";
  if (process.env.NODE_ENV === "test") return "test";
  return "unknown";
}

export function isPreviewEnv() {
  if (process.env.REACT_APP_DEBUG_DIAGNOSTICS === "1") return true;
  return currentEnvironment() === "preview"
    || currentEnvironment() === "development";
}


// ── Classification ─────────────────────────────────────────────────
/**
 * Classify any thrown value into one of the locked categories.
 * Pure; safe to call from tests.
 */
export function classifyError(err) {
  if (!err) return "unknown";

  // Render boundary helpers tag the error before re-throwing.
  if (err.__category && typeof err.__category === "string") {
    return err.__category;
  }

  // Webpack/CRA chunk failures — usually after a deploy.
  const name = err.name || "";
  const msg = (err.message || "").toLowerCase();
  if (
    name === "ChunkLoadError"
    || msg.includes("loading chunk")
    || msg.includes("loading css chunk")
    || msg.includes("failed to fetch dynamically imported module")
  ) {
    return "chunk_load";
  }

  // Axios-shaped error → use response.status when present.
  const status = err?.response?.status;
  if (typeof status === "number") {
    if (status === 401 || status === 403) return "auth";
    if (status === 408) return "timeout";
    if (status >= 500) return "server_5xx";
    if (status >= 400) return "client_4xx";
  }

  // Axios network/timeout without a response.
  if (err?.code === "ECONNABORTED" || msg.includes("timeout")) return "timeout";
  if (err?.code === "ERR_NETWORK"
      || msg.includes("network error")
      || (typeof navigator !== "undefined" && !navigator.onLine)) {
    return "network";
  }

  return "unknown";
}


// ── Build the payload from any caller shape ────────────────────────
// Upload-part URLs can exceed the ingestion model's 500-character limit.
// They also contain temporary signing credentials and private object keys.
// Keep the NDG endpoint path useful, but never send query strings or external
// resource paths to the diagnostics service (or its preview console echo).
function _read(value, key) {
  try { return value?.[key]; } catch (_) { return undefined; }
}

function _attempt(read, fallback = null) {
  try { return read(); } catch (_) { return fallback; }
}

function _safeUrl(value, maxLength = 500) {
  if (value == null || value === "") return null;
  try {
    const raw = String(value);
    const origin = _read(_read(typeof window !== "undefined" ? window : null, "location"), "origin")
      || "https://diagnostic.invalid";
    const url = new URL(raw, origin);
    if (url.protocol !== "https:" && url.protocol !== "http:") return "[redacted-url]";
    const trustedOrigins = new Set([origin]);
    try { if (API) trustedOrigins.add(new URL(API, origin).origin); } catch (_) { /* invalid config */ }
    const signed = [...url.searchParams.keys()].some(key =>
      /^(x-amz-|x-goog-|awsaccesskeyid$|googleaccessid$|signature$)/i.test(key));
    const external = !trustedOrigins.has(url.origin)
      || /(?:^|\.)r2\.cloudflarestorage\.com$/i.test(url.hostname) || signed;
    const prefix = /^(?:[a-z][a-z\d+.-]*:)?\/\//i.test(raw) ? url.origin : "";
    return `${prefix}${external ? "/[external-resource]" : url.pathname}`.slice(0, maxLength);
  } catch (_) {
    return "[invalid-url]";
  }
}

function _safeText(value, maxLength) {
  if (value == null) return null;
  return _attempt(() => String(value)
    .replace(/(?:https?:)?\/\/[^\s<>"'`]+/gi, url => _safeUrl(url))
    .replace(/(^|[\s("'=])((?:\/(?!\/)|\.{1,2}\/)[^\s<>"'`]*[?#][^\s<>"'`]*)/g,
      (_, prefix, url) => prefix + _safeUrl(url))
    .slice(0, maxLength));
}

function _safeExtras(value, seen = new WeakSet(), depth = 0) {
  if (value == null) return null;
  if (typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value === "string") return _safeText(value, 6000);
  if (typeof value !== "object") return null;
  if (depth >= 8) return "[truncated]";
  if (seen.has(value)) return "[circular]";
  seen.add(value);
  return _attempt(() => {
    if (Array.isArray(value)) {
      const length = _read(value, "length");
      if (!Number.isSafeInteger(length) || length < 0) return null;
      return Array.from({ length: Math.min(length, 100) }, (_, index) =>
        _safeExtras(_read(value, index), seen, depth + 1));
    }
    const result = Object.create(null);
    // Enumerating values eagerly invokes getters. Inspect keys first and read
    // each value separately, so one broken field cannot hide readable siblings.
    for (const key of Object.keys(value).slice(0, 100)) {
      if (/authorization|cookie|password|secret|credential|signature|token|x[-_]amz|upload[-_]?id|storage[-_]?key/i.test(key)) {
        result[key] = "[redacted]";
      } else {
        const item = _read(value, key);
        result[key] = typeof item === "string" && /(?:url|uri|href|route)$/i.test(key)
          ? _safeUrl(item)
          : _safeExtras(item, seen, depth + 1);
      }
    }
    return result;
  });
}

function _buildPayload(args, refId, category, error) {
  const request = _read(args, "request");
  const stack = _read(error, "stack") || _read(_read(error, "error"), "stack");
  const message = _read(error, "message") || error;
  const method = _safeText(_read(request, "method"), 10);
  const status = _read(request, "status") ?? _read(_read(error, "response"), "status");
  const extras = _safeExtras(_read(args, "extras"));
  const browser = typeof window !== "undefined" ? window : null;
  return {
    client_ref_id: refId,
    category,
    environment: _safeText(_attempt(currentEnvironment), 24),
    app_version: _safeText(APP_VERSION, 64),
    route: _safeUrl(
      _read(args, "route") || _read(_read(browser, "location"), "pathname"), 200),
    component: _safeText(_read(args, "component"), 120),
    request_url: _safeUrl(_read(request, "url")),
    request_method: method ? method.toUpperCase().slice(0, 10) : null,
    request_status: Number.isSafeInteger(status) ? status : null,
    message: _safeText(message, 600),
    stack: _safeText(stack, 6000),
    user_agent:
      typeof navigator !== "undefined" ? _safeText(_read(navigator, "userAgent"), 400) : null,
    viewport: browser
      ? _safeText(_attempt(() => `${_read(browser, "innerWidth")}x${_read(browser, "innerHeight")}`), 40)
      : null,
    extras: extras && typeof extras === "object" && !Array.isArray(extras) ? extras : null,
  };
}


// ── The fire-and-forget reporter ───────────────────────────────────
/**
 * @returns {Promise<{ ref_id: string }>} — resolved with the local
 *   ref ID even if the network POST fails. Never rejects.
 */
export async function reportClientError(args = {}) {
  // Never enumerate the caller or let an unreadable getter/proxy prevent the
  // local recovery reference from reaching the UI.
  const refId = _safeText(_read(args, "refId"), 40) || generateRefId();
  const error = _read(args, "error");
  const category = _safeText(_read(args, "category"), 40)
    || _safeText(_attempt(() => classifyError(error)), 40) || "unknown";
  const payload = _attempt(() => _buildPayload(args, refId, category, error), {
    client_ref_id: refId,
    category,
  });

  // Tag the original error object so callers can read it back.
  try {
    if (error && typeof error === "object") {
      error.refId = refId;
      error.category = category;
    }
  } catch (_) { /* read-only objects → ignore */ }

  _attempt(() => {
    if (typeof console !== "undefined" && isPreviewEnv()) {
      // Preview-only console echo so devs notice immediately.
      // eslint-disable-next-line no-console
      console.warn("[clientErrorReporter]", refId, category, payload.message, payload);
    }
  });

  if (!API) return { ref_id: refId };

  try {
    await axios.post(`${API}/api/debug/client-errors`, payload, {
      withCredentials: true,
      timeout: 5000,
      // Tag this request so the axios error interceptor (installed in
      // index.js) does NOT try to report a failure-to-report — that
      // would risk a feedback loop on a flaky network.
      headers: { "x-ndg-internal": "client-error-report" },
    });
  } catch (_) {
    // Never re-raise. The local ref ID is enough.
  }
  return { ref_id: refId };
}


// ── Public taxonomy export ────────────────────────────────────────
export const CATEGORIES = Object.freeze([
  "network",
  "auth",
  "timeout",
  "server_5xx",
  "client_4xx",
  "render_crash",
  "stale_active_operation",
  "chunk_load",
  "unhandled_promise",
  "work_mode_trace",
  "unknown",
]);


export default reportClientError;
